import { type FC } from "react";
import { Download, MoreHorizontal } from "lucide-react";
import { format } from "date-fns";

import type { PlaidConnection } from "@/shared/types/estateTypes";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

import { SyncStatus } from "./SyncStatus";

interface ConnectedAccountCardProps {
  connection: PlaidConnection;
  estateDisplayName: string;
  onRefresh: () => void;
  onReconnect: () => void;
  isRefreshing: boolean;
  refreshResult?: { refreshed_count: number } | null;
  onDisconnect?: () => void;
  onExport?: () => void;
  isExporting?: boolean;
}

export const ConnectedAccountCard: FC<ConnectedAccountCardProps> = ({
  connection,
  estateDisplayName,
  onRefresh,
  onReconnect,
  isRefreshing,
  refreshResult,
  onDisconnect,
  onExport,
  isExporting,
}) => {
  const rawName = connection.account_name ?? connection.institution_name ?? "Estate account";
  const displayName = rawName.replace(/\s*\(\.\.\.\d+\)$/, "");
  const maskMatch = rawName.match(/\(\.\.\.(\d+)\)$/);
  const accountMask = maskMatch ? maskMatch[1] : null;
  const firstLetter = (connection.institution_name ?? displayName).charAt(0).toUpperCase();
  const logoSrc = connection.plaid_institution_logo
    ? `data:image/png;base64,${connection.plaid_institution_logo}`
    : null;
  const openedDate = connection.created_at
    ? format(new Date(connection.created_at), "MMM d, yyyy")
    : null;

  return (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-10">
      <div className="flex flex-1 items-start gap-3 min-w-0 sm:items-center sm:gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[5px] bg-subtle">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={`${connection.institution_name ?? displayName} logo`}
              className="h-8 w-8 object-contain"
            />
          ) : (
            <span className="text-base font-medium text-primary">{firstLetter}</span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="text-base font-medium text-primary truncate">{displayName}</p>
          <p className="text-sm text-secondary">
            {estateDisplayName}
            {accountMask ? (
              <span> · ••••{accountMask}</span>
            ) : null}
            {openedDate ? (
              <span> · Opened {openedDate}</span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <div className="flex-1 sm:flex-none">
          <SyncStatus
            connection={connection}
            onRefresh={onRefresh}
            onReconnect={onReconnect}
            isRefreshing={isRefreshing}
            refreshResult={refreshResult}
          />
        </div>
        <Button variant="outline" onClick={onExport} disabled={isExporting}>
          <Download className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">{isExporting ? "Exporting..." : "Export"}</span>
        </Button>
        {onDisconnect && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="md" className="px-2.5" aria-label="Account options">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onDisconnect} className="text-destructive">
                Disconnect account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
